import { Container, Sprite, TilingSprite } from 'pixi.js';
import { getTexture, makeSprite } from '@/assets/resolver';
import { MAPS } from '@/config/maps';
import { isHorizontal, type Door } from './geometry';

/** Field texture height (px) — the shield strip is drawn this thick */
const FIELD_TEX_H = 64;
/** Field scroll speed (px/s) and flicker */
const SCROLL = 90;
const FLICKER_HZ = 9;
/** Pylon tint once the shield is down (dimmed lamps) */
const PYLON_OFF = 0x6c6c7a;

/**
 * One doorway's shield (docs/01 gates): a scrolling energy strip
 * between two pylons. Closed → bright + flickering; opening → it dissolves
 * (thins and fades over MAPS.gates.dissolve); re-sealing snaps it back.
 * Reads Door.closed / fadeT only — the logic lives in run/advance-director.
 */
export class GateView {
  readonly view = new Container();
  private readonly field: TilingSprite;
  private readonly pylons: Sprite[] = [];

  constructor(private readonly door: Door, private readonly tint: number) {
    const g = door.gate;
    const horiz = isHorizontal(door.dir);
    const length = horiz ? g.h : g.w;
    this.field = new TilingSprite({
      texture: getTexture('env/gate-field'), width: length, height: FIELD_TEX_H,
    });
    this.field.anchor.set(0.5);
    this.field.position.set(g.x + g.w / 2, g.y + g.h / 2);
    this.field.rotation = horiz ? Math.PI / 2 : 0;
    this.field.tint = tint;
    this.view.addChild(this.field);
    for (const side of [-1, 1]) {
      const p = makeSprite('env/gate-pylon');
      p.position.set(
        g.x + g.w / 2 + (horiz ? 0 : side * g.w / 2),
        g.y + g.h / 2 + (horiz ? side * g.h / 2 : 0),
      );
      p.rotation = horiz ? Math.PI / 2 : 0;
      p.tint = tint;
      this.pylons.push(p);
      this.view.addChild(p);
    }
  }

  render(time: number): void {
    const d = this.door;
    const G = MAPS.gates;
    this.field.tilePosition.x = time * SCROLL;
    let k: number;
    if (d.closed) {
      k = Math.min(1, d.fadeT / G.seal);
      this.field.alpha = k * (0.78 + 0.12 * Math.sin(time * FLICKER_HZ * Math.PI * 2));
    } else {
      k = 1 - Math.min(1, d.fadeT / G.dissolve);
      this.field.alpha = k * k * 0.9;
    }
    this.field.visible = this.field.alpha > 0.01;
    this.field.scale.y = 0.25 + 0.75 * k;
    for (const p of this.pylons) p.tint = d.closed ? this.tint : PYLON_OFF;
  }
}
